import React, { useMemo, useState, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import {
    Badge,
    Radio,
    Spinner,
    Text,
    Tooltip,
    makeStyles,
    tokens,
} from "@fluentui/react-components";
import {
    ArrowUp16Regular,
    ArrowDown16Regular,
    ArrowSort16Regular,
    ErrorCircle16Regular,
    Person16Regular,
} from "@fluentui/react-icons";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
import AdminService from "../AdminService";
import TablePagination from "../TablePagination";

// ── Column name → API sortBy field ────────────────────────────────────────────
const SORT_FIELD_MAP = {
    name:     "firstName",
    username: "username",
    email:    "email",
};

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
    },
    tableScroll: {
        flex: 1,
        overflowY: "auto",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        background: tokens.colorNeutralBackground1,
        "& th, & td": {
            padding: "6px 8px",
            borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
            textAlign: "left",
        },
        "& thead th": {
            background: tokens.colorNeutralBackground2,
            fontWeight: 600,
        },
    },
    tableHeader: {
        position: "sticky",
        top: 0,
        zIndex: 2,
    },
    radioCell: {
        display: "flex",
        alignItems: "center",
        margin: 0,
    },
    row: {
        cursor: "pointer",
        ":hover td": {
            backgroundColor: tokens.colorSubtleBackgroundHover,
        },
    },
    rowSelected: {
        "& td": {
            backgroundColor: tokens.colorSubtleBackgroundSelected,
        },
    },
    // Pinned (currently-assigned) row rendered above the paginated results.
    // Sticky so it stays visible when the table body scrolls; a distinct
    // border-bottom separates it from the normal paginated rows.
    rowPinned: {
        cursor: "pointer",
        position: "sticky",
        top: 0,
        zIndex: 1,
        "& td": {
            backgroundColor: tokens.colorBrandBackground2,
            borderBottom: `2px solid ${tokens.colorBrandStroke2}`,
            fontWeight: 500,
        },
        ":hover td": {
            backgroundColor: tokens.colorBrandBackground2Hover,
        },
    },
    assignedBadge: {
        marginLeft: "8px",
    },
    paginationWrapper: {
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        background: tokens.colorNeutralBackground1,
        flexShrink: 0,
        padding: "0 8px",
    },
    stateCenter: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        padding: "48px 24px",
        textAlign: "center",
        flex: 1,
    },
    stateText: {
        fontSize: "13px",
        color: tokens.colorNeutralForeground3,
        maxWidth: "280px",
    },
});

// ── Column helper ─────────────────────────────────────────────────────────────

const columnHelper = createColumnHelper();

// ── Data-fetching hook ────────────────────────────────────────────────────────

/**
 * Fetches active Salesforce users for the given SF org with server-side
 * search, sorting, and pagination.
 */
function useSfOrgUsers(sfOrgId, { search = "", sortBy = "username", sortOrder = "asc", page = 1, pageSize = 25, searchTenantId = undefined, pinnedSfUserId = undefined } = {}) {
    const [users,      setUsers]      = useState([]);
    const [pinnedUser, setPinnedUser] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [loading,    setLoading]    = useState(false);
    const [error,      setError]      = useState(null);

    useEffect(() => {
        if (!sfOrgId) {
            setUsers([]);
            setPinnedUser(null);
            setTotalCount(0);
            setError(null);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        AdminService.getSFUsersOfOrg(sfOrgId, { search, sortBy, sortOrder, page, pageSize, searchTenantId, pinnedSfUserId })
            .then((res) => {
                if (cancelled) return;
                if (!res.isAuthorized) {
                    setError("Not authorized to view users for this Salesforce org.");
                    setUsers([]);
                    setPinnedUser(null);
                    setTotalCount(0);
                    return;
                }
                // Server already filters by isActive:true. Additionally guard against
                // users with no sfUserId — those can't be used for assignment and would
                // break TanStack Table's getRowId (returns undefined).
                const mapUser = (u) => ({
                    sfUserId: u.sfUserId,
                    username: u.username,
                    email:    u.email,
                    name:     (`${u.firstName ?? ""} ${u.lastName ?? ""}`).trim() || u.username,
                    isActive: u.isActive,
                });
                const mapped = (res.data ?? [])
                    .filter((u) => u.isActive && u.sfUserId)
                    .map(mapUser);
                setUsers(mapped);
                // The pinned user is intentionally NOT filtered by isActive so the
                // admin can still see who is currently assigned even after that
                // user has been deactivated in Salesforce.
                setPinnedUser(res.pinnedUser && res.pinnedUser.sfUserId ? mapUser(res.pinnedUser) : null);
                setTotalCount(res.pagination?.total ?? mapped.length);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.message || "Failed to load Salesforce users.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [sfOrgId, search, sortBy, sortOrder, page, pageSize, searchTenantId, pinnedSfUserId]);

    return { users, pinnedUser, totalCount, loading, error };
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * SfUserPickerTable
 *
 * Shared single-select table for picking a Salesforce user within a given org.
 * Fetches live data from the API with server-side search, sorting, and pagination.
 * Columns: Radio · Name · Username · Email
 *
 * @param {object}      props
 * @param {string|null} props.sfOrgId          - Salesforce org ID used to fetch users
 * @param {string|null} props.selectedUserId   - Controlled: sfUserId of the selected row
 * @param {function}    props.onSelect         - Called with the full user object on selection
 * @param {string}      props.filter           - Search string from parent (debounced internally)
 * @param {string}      [props.searchTenantId] - When set, scopes the API call to a specific
 *                                               tenant (cross-tenant admin view).
 * @param {string}      [props.pinnedUserId]   - sfUserId of the currently-assigned user to
 *                                               render pinned at the top of the table. Kept
 *                                               visible regardless of page/search/sort so the
 *                                               admin always sees the current assignment.
 */
export default function SfUserPickerTable({
    sfOrgId        = null,
    selectedUserId = null,
    onSelect,
    filter         = "",
    searchTenantId = undefined,
    pinnedUserId   = null,
}) {
    const styles = useStyles();
    const isFirstFilterRender = useRef(true);
    // ── Measure the sticky thead height so the pinned row sticks below it ─────
    // Without this, both the thead and the pinned <tr> share top:0 and the
    // pinned row slides underneath the header when scrolling.
    const theadRef                    = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    useLayoutEffect(() => {
        const el = theadRef.current;
        if (!el) return;
        const ro = new window.ResizeObserver(() => setHeaderHeight(el.offsetHeight));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    // ── Local state ────────────────────────────────────────────────────────
    const [sorting,         setSorting]         = useState([]);          // [{id, desc}]
    const [pagination,      setPagination]      = useState({ pageIndex: 0, pageSize: 25 });
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // ── Debounce the parent filter prop (500 ms); reset to page 1 ─────────
    useEffect(() => {
        // Skip the very first render so we don't fire an extra fetch on mount.
        if (isFirstFilterRender.current) {
            isFirstFilterRender.current = false;
            return;
        }
        const t = setTimeout(() => {
            setDebouncedSearch(filter);
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }, 500);
        return () => clearTimeout(t);
    }, [filter]);

    // Reset pagination when sfOrgId changes (new org = start from page 1).
    // Does NOT reset isFirstFilterRender: that guard is only for the initial
    // mount and must not be re-armed on org switches, otherwise the first
    // filter keystroke after an org change would be silently skipped.
    useEffect(() => {
        setPagination({ pageIndex: 0, pageSize: 25 });
        setSorting([]);
        setDebouncedSearch("");
    }, [sfOrgId]);

    // ── Derive API sort params from TanStack sort state ────────────────────
    const sortBy    = SORT_FIELD_MAP[sorting[0]?.id] ?? "username";
    const sortOrder = sorting[0]?.desc ? "desc" : "asc";

    // ── Fetch ──────────────────────────────────────────────────────────────
    const { users, pinnedUser, totalCount, loading, error } = useSfOrgUsers(sfOrgId, {
        search:    debouncedSearch,
        sortBy,
        sortOrder,
        page:      pagination.pageIndex + 1,
        pageSize:  pagination.pageSize,
        searchTenantId,
        pinnedSfUserId: pinnedUserId ?? undefined,
    });

    // Remove the pinned user from the paginated list to avoid a duplicate row.
    // The pinned row is always rendered separately at the top of the table.
    const dedupedUsers = useMemo(() => {
        if (!pinnedUser?.sfUserId) return users;
        return users.filter((u) => u.sfUserId !== pinnedUser.sfUserId);
    }, [users, pinnedUser]);

    // ── Row-select callback ────────────────────────────────────────────────
    const handleRowSelect = useCallback((user) => onSelect?.(user), [onSelect]);

    // ── Column definitions ─────────────────────────────────────────────────
    const columns = useMemo(
        () => [
            // Radio / single-select
            columnHelper.display({
                id: "select",
                size: 44,
                enableSorting: false,
                header: () => null,
                cell: ({ row }) => (
                    <div className={styles.radioCell}>
                        <Radio
                            checked={selectedUserId === row.original.sfUserId}
                            onChange={() => handleRowSelect(row.original)}
                            value={row.original.sfUserId}
                            aria-label={`Select user ${row.original.name}`}
                        />
                    </div>
                ),
            }),

            // Name
            columnHelper.accessor("name", {
                header: "Name",
                cell: (info) => (
                    <strong style={{ fontSize: "13px" }}>{info.getValue()}</strong>
                ),
            }),

            // Username
            columnHelper.accessor("username", {
                header: "Username",
                cell: (info) => (
                    <span style={{ fontSize: "12px", wordBreak: "break-all" }}>
                        {info.getValue()}
                    </span>
                ),
            }),

            // Email
            columnHelper.accessor("email", {
                header: "Email",
                cell: (info) => (
                    <span style={{ fontSize: "12px", wordBreak: "break-all" }}>
                        {info.getValue()}
                    </span>
                ),
            }),
        ],
        // Re-memoize when selection changes so the Radio checked state stays in sync.
        [selectedUserId, handleRowSelect],
    );

    // ── TanStack Table (server-side everything) ───────────────────────────
    const table = useReactTable({
        data:               dedupedUsers,
        columns,
        state:              { sorting, pagination },
        onSortingChange:    setSorting,
        onPaginationChange: setPagination,
        getCoreRowModel:    getCoreRowModel(),
        manualPagination:   true,
        manualSorting:      true,
        manualFiltering:    true,
        pageCount:          Math.ceil(totalCount / pagination.pageSize) || 1,
        enableMultiSort:    false,
        getRowId:           (row) => row.sfUserId,
    });

    // ── Early-exit states ──────────────────────────────────────────────────
    if (!sfOrgId) {
        return (
            <div className={styles.stateCenter}>
                <Person16Regular style={{ width: 32, height: 32, color: tokens.colorNeutralForeground3, opacity: 0.5 }} />
                <Text className={styles.stateText}>No Salesforce org linked to this queue.</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.stateCenter}>
                <ErrorCircle16Regular style={{ width: 32, height: 32, color: tokens.colorPaletteRedForeground1 }} />
                <Text className={styles.stateText}>{error}</Text>
            </div>
        );
    }

    // ── Table + pagination ─────────────────────────────────────────────────
    return (
        <div className={styles.root}>
            {/* Scroll area */}
            {loading ? (
                <div className={styles.stateCenter}>
                    <Spinner size="medium" />
                    <Text className={styles.stateText}>Loading Salesforce users...</Text>
                </div>
            ) : (
                <div className={styles.tableScroll}>
                    <table className={styles.table}>
                        <thead ref={theadRef} className={styles.tableHeader}>
                            {table.getHeaderGroups().map((hg) => (
                                <tr key={hg.id}>
                                    {hg.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            style={{
                                                width:  header.column.columnDef.size ?? undefined,
                                                cursor: header.column.getCanSort() ? "pointer" : "default",
                                            }}
                                            onClick={(e) => header.column.getToggleSortingHandler()?.(e)}
                                        >
                                            <span style={{ display: "inline-flex", alignItems: "center" }}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {header.column.getCanSort() && (
                                                    <Tooltip
                                                        content={
                                                            header.column.getIsSorted()
                                                                ? `Sorted ${header.column.getIsSorted() === "asc" ? "ascending" : "descending"}.`
                                                                : "Click to sort."
                                                        }
                                                        relationship="label"
                                                    >
                                                        <span style={{ display: "inline-flex", alignItems: "center", marginLeft: 4 }}>
                                                            {header.column.getIsSorted() === "asc"  && <ArrowUp16Regular />}
                                                            {header.column.getIsSorted() === "desc" && <ArrowDown16Regular />}
                                                            {!header.column.getIsSorted()           && <ArrowSort16Regular />}
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>

                        <tbody>
                            {/*
                              Pinned row for the currently-assigned user. Always rendered
                              at the top so the admin can see who is currently assigned
                              regardless of page/search/sort. Duplicates against the
                              paginated results are removed via `dedupedUsers` above.
                            */}
                            {pinnedUser && (
                                <tr
                                    key={`pinned-${pinnedUser.sfUserId}`}
                                    className={styles.rowPinned}
                                    style={{ top: headerHeight }}
                                    onClick={() => handleRowSelect(pinnedUser)}
                                >
                                    <td>
                                        <div className={styles.radioCell}>
                                            <Radio
                                                checked={selectedUserId === pinnedUser.sfUserId}
                                                onChange={() => handleRowSelect(pinnedUser)}
                                                value={pinnedUser.sfUserId}
                                                aria-label={`Select user ${pinnedUser.name}`}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <strong style={{ fontSize: "13px" }}>{pinnedUser.name}</strong>
                                        <Badge
                                            appearance="filled"
                                            color="brand"
                                            size="small"
                                            className={styles.assignedBadge}
                                        >
                                            Currently assigned
                                        </Badge>
                                        {pinnedUser.isActive === false && (
                                            <Badge
                                                appearance="outline"
                                                color="danger"
                                                size="small"
                                                className={styles.assignedBadge}
                                            >
                                                Inactive
                                            </Badge>
                                        )}
                                    </td>
                                    <td>
                                        <span style={{ fontSize: "12px", wordBreak: "break-all" }}>
                                            {pinnedUser.username}
                                        </span>
                                    </td>
                                    <td>
                                        <span style={{ fontSize: "12px", wordBreak: "break-all" }}>
                                            {pinnedUser.email}
                                        </span>
                                    </td>
                                </tr>
                            )}

                            {table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className={
                                            selectedUserId === row.original.sfUserId
                                                ? styles.rowSelected
                                                : styles.row
                                        }
                                        onClick={() => handleRowSelect(row.original)}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                !pinnedUser && (
                                    <tr>
                                        <td
                                            colSpan={columns.length}
                                            style={{ textAlign: "center", padding: "32px 16px" }}
                                        >
                                            <Text style={{ color: tokens.colorNeutralForeground3 }}>
                                                {debouncedSearch
                                                    ? "No users match your search."
                                                    : "No active users found for this org."}
                                            </Text>
                                        </td>
                                    </tr>
                                )
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination — always visible once org is set */}
            <div className={styles.paginationWrapper}>
                <TablePagination
                    table={table}
                    totalCount={totalCount}
                    pagination={pagination}
                    setPagination={setPagination}
                    disabled={loading}
                />
            </div>
        </div>
    );
}
